/**
 * apps/admin API access.
 *
 * Every request shape comes from `packages/api-types`, which is GENERATED from
 * contracts/openapi.yaml — so a contract change that breaks this panel breaks its
 * typecheck rather than a hokimiyat staff member's afternoon.
 *
 * The session is the `__Host-nvadmin` cookie: HttpOnly, Secure, SameSite=Lax,
 * same-origin. JS never reads or writes it — `credentials: 'include'` is the
 * whole client-side auth story, which is exactly why the `__Host-` prefix and the
 * same-origin Caddy deployment were chosen (§7.2).
 */
import { createNavoiyClient, isProblem, type NavoiyClient, type Problem } from '@navoiy/api-types';

const API_BASE = import.meta.env.VITE_API_BASE ?? '/api/v1';

export function apiBaseUrl(): string {
  return new URL(API_BASE, window.location.origin).toString().replace(/\/$/, '');
}

/** An absolute path for `<audio src>` / `<a download href>` (§6.4 streaming route). */
export function mediaUrl(murojaatId: string): string {
  return `${apiBaseUrl()}/admin/murojaats/${encodeURIComponent(murojaatId)}/media`;
}

export function staffPhotoUrl(mediaId: string): string {
  return `${apiBaseUrl()}/media/staff/${encodeURIComponent(mediaId)}`;
}

let client: NavoiyClient | null = null;

export function api(): NavoiyClient {
  if (client) return client;
  client = createNavoiyClient({
    baseUrl: apiBaseUrl(),
    // Dev and preview run same-origin over http; production is https-only (§10.3).
    allowInsecure: window.location.protocol === 'http:',
    credentials: 'include',
  });
  return client;
}

/**
 * The panel's error type. `status` is carried separately because a 401 has to
 * bounce the user to the login screen no matter what body came with it.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly problem: Problem | null;
  readonly retryAfter: number | null;

  constructor(status: number, problem: Problem | null, retryAfter: number | null) {
    super(problem?.detail ?? problem?.title ?? `HTTP ${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.problem = problem;
    this.retryAfter = retryAfter;
  }

  /** The RFC 9457 machine-readable code (§3.1), when the server sent one. */
  get code(): Problem['code'] | null {
    return this.problem?.code ?? null;
  }

  /** First `errors[]` entry's code for a given field, e.g. `last_admin`. */
  fieldCode(field: string): string | null {
    return this.problem?.errors?.find((e) => e.field === field)?.code ?? null;
  }

  /** Any `errors[]` code, regardless of field — the §4.4 outcomes name the code. */
  hasErrorCode(code: string): boolean {
    return this.problem?.errors?.some((e) => e.code === code) ?? false;
  }
}

interface FetchResult<T> {
  data?: T;
  error?: unknown;
  response: Response;
}

/**
 * Unwraps an openapi-fetch result into data or a thrown `ApiError`.
 * Screens then handle one error type instead of two shapes.
 */
export function unwrap<T>(result: FetchResult<T>): T {
  if (result.error !== undefined || !result.response.ok) {
    const problem = isProblem(result.error) ? result.error : null;
    const retryAfterHeader = result.response.headers.get('Retry-After');
    const retryAfter = retryAfterHeader ? Number.parseInt(retryAfterHeader, 10) : null;
    throw new ApiError(
      result.response.status,
      problem,
      Number.isFinite(retryAfter) ? retryAfter : null,
    );
  }
  return result.data as T;
}

/**
 * Narrows an operation that can answer with EITHER JSON or `text/csv`.
 *
 * The §14.2 stats endpoints declare both content types on their `200`, so the
 * generated type of their body is `T | string` — correct, and unhelpful at a
 * call site that never sends `?format=csv`. This asserts the branch the panel
 * actually asked for instead of casting the union away, so if a screen ever
 * does request CSV and then tries to read `.items` off it, the failure is a
 * thrown error naming the cause rather than `undefined` propagating into a
 * chart.
 *
 * The CSV branch is reached by a direct download link, never by this client.
 */
export function jsonBody<T>(body: T | string): T {
  if (typeof body === 'string') {
    throw new ApiError(200, null, null);
  }
  return body;
}

/** Maps an `ApiError` onto an `admin.error.*` i18n key (§3.1 code table). */
export function errorKey(error: unknown): { key: string; values?: Record<string, string | number> } {
  if (!(error instanceof ApiError)) return { key: 'admin.error.network' };
  if (error.hasErrorCode('last_admin')) return { key: 'admin.error.lastAdmin' };
  if (error.hasErrorCode('not_allowed_for_role')) return { key: 'admin.error.notAllowedForRole' };
  if (error.hasErrorCode('taken')) return { key: 'admin.error.taken' };
  switch (error.code) {
    case 'forbidden':
      return error.problem?.detail === 'self_role_change'
        ? { key: 'admin.error.selfRoleChange' }
        : { key: 'admin.error.forbidden' };
    case 'invalid_transition':
      return { key: 'admin.error.invalidTransition' };
    case 'validation_failed':
      return { key: 'admin.error.validation' };
    case 'not_found':
      return { key: 'admin.error.notFound' };
    case 'payload_too_large':
      return { key: 'admin.error.tooLarge' };
    case 'unsupported_media_type':
      return { key: 'admin.error.unsupportedType' };
    case 'rate_limited':
      return { key: 'admin.error.rateLimited', values: { seconds: error.retryAfter ?? 60 } };
    default:
      return { key: 'admin.error.generic' };
  }
}
